/**
 * Custom Encoding panel on ready-made products (#1472; replaces the #1119 popup).
 *
 * Encoding is a Shopify variant option: the "Custom Encoding" variant is the paid
 * fact, priced and SKU'd on its own. On a product form marked data-nfc-encoding="true"
 * (snippets/nfc-encoding-attrs.liquid) the panel (snippets/nfc-encoding-field.liquid,
 * under the variant picker, outside the form) opens while the selected variant
 * encodes:
 * - NDEF chips: the customer types a link or text, sees it against the chip's memory
 *   (a hint read from Studio; the paid order is re-checked on the server) and saves
 *   it. Saving closes the panel and marks the Custom Encoding pill; clicking that
 *   pill again reopens it. Cancel selects "No Encoding".
 * - MIFARE Classic: nothing is typed. The customer sends a sector data file after
 *   ordering; the line is added without a value and the server holds it for review.
 * The saved value is copied into the hidden cart-line inputs inside the form
 * (snippets/nfc-encoding-form-inputs.liquid), which are enabled only then, so a
 * "No Encoding" line never carries a value. Add to Cart is held until an NDEF line
 * has a value that fits; a valid value typed but not saved is saved by the click.
 * When the chip cannot be encoded or the limits cannot be loaded, nothing is added.
 * A product card's direct add has no panel, so it opens the product page instead.
 */
(function () {
  'use strict';

  var LOAD_TIMEOUT_MS = 3000;
  var SAVED_FLASH_MS = 900;
  var HOST_SELECTOR = 'product-form-component[data-nfc-encoding="true"]';
  // The product page grid, or the Quick add modal built from it: the panel and the
  // form are matched inside it, never through a form="" id the modal may duplicate.
  var SCOPE_SELECTOR = '[data-product-grid-content], #quick-add-modal-content';
  var TEMPLATE_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  /** undefined = not loaded yet; null = failed; otherwise { storeChipName: bytes }. */
  var capacities;
  var capacitiesPromise = null;
  /** Per panel: { tab, saved, open, flash, strict }. */
  var states = new WeakMap();

  function resolveApp() {
    var app = typeof window.NfckeyResolveStudioApp === 'function' ? window.NfckeyResolveStudioApp() : null;
    return app && app.key && app.proxyPath ? app : null;
  }

  function loadCapacities(app) {
    if (capacitiesPromise) return capacitiesPromise;
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = setTimeout(function () { if (controller) controller.abort(); }, LOAD_TIMEOUT_MS);
    // same-origin, not omit: a password-protected storefront (the dev store) redirects a
    // cookieless App Proxy request to /password.
    capacitiesPromise = fetch(app.proxyPath + '/api/embed/chip-limits', {
      credentials: 'same-origin',
      signal: controller ? controller.signal : undefined,
    })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (json) {
        return json && json.capacities && typeof json.capacities === 'object' ? json.capacities : null;
      })
      .catch(function () { return null; })
      .then(function (result) {
        clearTimeout(timer);
        capacities = result;
        capacitiesPromise = null;
        return result;
      });
    return capacitiesPromise;
  }

  function parse(json) {
    try { return JSON.parse(json || '{}') || {}; } catch (e) { return {}; }
  }

  function stateOf(panel) {
    var st = states.get(panel);
    if (!st) {
      st = { tab: 'link', saved: null, open: false, flash: false, strict: false };
      states.set(panel, st);
    }
    return st;
  }

  function partsOf(host) {
    var form = host.querySelector('form');
    if (!form) return null;
    // The mobile Quick add keeps only the picker and the form, so no panel: the
    // click handler then sends the customer to the product page.
    var scope = host.closest(SCOPE_SELECTOR);
    var find = function (sel) { return scope ? scope.querySelector(sel) : null; };
    var panel = find('[data-nfc-panel]');
    var inPanel = function (sel) { return panel ? panel.querySelector(sel) : null; };
    return {
      form: form,
      scope: scope,
      panel: panel,
      ndef: inPanel('[data-nfc-ndef]'),
      mifare: inPanel('[data-nfc-mifare]'),
      customize: find('[data-nfc-customize]'),
      link: find('[data-nfc-customize-link]'),
      quantity: find('input[name="quantity"]'),
      valueInput: form.querySelector('[data-nfc-value-input]'),
      appKey: form.querySelector('[data-nfc-app-key]'),
    };
  }

  function complete(p) {
    return Boolean(p.panel && p.ndef && p.mifare && p.valueInput && p.appKey);
  }

  /** The product form host that a panel (outside the form) belongs to. */
  function hostOf(el) {
    var scope = el.closest(SCOPE_SELECTOR);
    return scope ? scope.querySelector(HOST_SELECTOR) : null;
  }

  function selectedVariantId(form) {
    var input = form.querySelector('[name="id"]');
    return input ? String(input.value) : '';
  }

  function encodes(host, variantId) {
    return parse(host.dataset.nfcEncodes)[variantId] === true;
  }

  function inputOf(p, tab) {
    return p.panel.querySelector('[data-nfc-input="' + tab + '"]');
  }

  /** The active tab's value, as it would be written; a bare domain on the link tab gets https://. */
  function currentValue(p, st) {
    var input = inputOf(p, st.tab);
    var value = input ? input.value.trim() : '';
    if (st.tab === 'link' && value && !/^[a-z][a-z0-9+.-]*:/i.test(value)) value = 'https://' + value;
    return value;
  }

  /** The verdict for the current value: { ok, reason, value?, bytes?, capacity?, chip?, mifare? }. */
  function verdict(host, p, st, variantId) {
    var chip = parse(host.dataset.nfcChips)[variantId];
    var size = window.NfckeyEncodingSize;
    if (chip && size && size.isSectorFileChip(chip)) return { ok: true, reason: null, mifare: true, chip: chip };
    if (!resolveApp() || !chip || !size) return { ok: false, reason: 'unavailable' };
    if (capacities === undefined) return { ok: false, reason: 'loading' };
    var capacity = capacities ? capacities[chip] : null;
    if (typeof capacity !== 'number' || capacity <= 0) return { ok: false, reason: 'unavailable' };
    var value = currentValue(p, st);
    var v = size.checkValue(value, capacity);
    var reason = v.reason;
    if (v.ok && st.tab === 'link' && size.valueKind(value) !== 'link') reason = 'not_link';
    return { ok: v.ok && !reason, reason: reason, value: value, bytes: v.bytes, capacity: capacity, chip: chip };
  }

  function savedValid(st, v) {
    return Boolean(v.ok && !v.mifare && st.saved !== null && v.value === st.saved);
  }

  function statusText(v, st) {
    if (v.reason === 'unavailable') {
      return 'NFC encoding is not available for this chip right now. Choose "No Encoding" to add the keys without it.';
    }
    if (v.reason === 'loading') return 'Checking how much this chip holds…';
    if (v.reason === 'invalid') {
      return 'Use one line of text without line breaks or control characters.';
    }
    if (v.reason === 'not_link') return 'Enter a full web address, like https://yourwebsite.com.';
    if (v.reason === 'empty' && st.strict) {
      return 'Enter a link or text and save it, or choose "No Encoding".';
    }
    if (st.strict && v.ok) return 'Save the encoding before adding to cart.';
    return '';
  }

  function keysInOrder(host, p, variantId) {
    var size = window.NfckeyEncodingSize;
    var title = parse(host.dataset.nfcTitles)[variantId];
    var perUnit = size && title ? size.keysPerUnit(title) : 1;
    var qty = p.quantity ? parseInt(p.quantity.value, 10) : 1;
    return perUnit * (qty > 0 ? qty : 1);
  }

  function render(host, p, variantId, on) {
    var panel = p.panel;
    var st = stateOf(panel);
    var v = on ? verdict(host, p, st, variantId) : { ok: false, reason: null };
    var valid = on && savedValid(st, v);

    panel.hidden = !on || !(v.mifare || st.open || st.flash || !valid);
    panel.toggleAttribute('data-saved', valid);
    p.ndef.hidden = Boolean(v.mifare);
    p.mifare.hidden = !v.mifare;

    p.appKey.disabled = !on;
    p.valueInput.disabled = !valid;
    p.valueInput.value = valid ? st.saved : '';
    if (on) {
      var app = resolveApp();
      p.appKey.value = app ? app.key : '';
    }
    if (!on || v.mifare) return;

    // Tabs
    panel.querySelectorAll('[data-nfc-tab]').forEach(function (tab) {
      tab.setAttribute('aria-selected', String(tab.dataset.nfcTab === st.tab));
    });
    panel.querySelectorAll('[data-nfc-form]').forEach(function (form) {
      form.hidden = form.dataset.nfcForm !== st.tab;
    });
    var unavailable = v.reason === 'unavailable';
    panel.querySelectorAll('[data-nfc-input]').forEach(function (input) { input.disabled = unavailable; });
    var input = inputOf(p, st.tab);
    var bad = v.reason === 'invalid' || v.reason === 'not_link' || v.reason === 'too_long' ||
      (st.strict && v.reason === 'empty');
    if (input) {
      if (bad) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
    }

    // Meter
    var count = panel.querySelector('[data-nfc-meter-count]');
    var fill = panel.querySelector('[data-nfc-meter-fill]');
    var left = panel.querySelector('[data-nfc-meter-left]');
    var cap = v.capacity || 0;
    var used = v.bytes || 0;
    var pct = cap ? Math.round((used / cap) * 100) : 0;
    var tone = v.reason === 'too_long' ? 'over' : pct >= 80 ? 'warn' : '';
    if (count) count.textContent = cap ? used + ' / ' + cap + ' bytes' : '';
    if (fill) {
      fill.style.width = Math.min(pct, 100) + '%';
      if (tone) fill.dataset.tone = tone; else delete fill.dataset.tone;
    }
    if (left) {
      left.textContent = !cap ? '' : used === 0 ? cap + ' bytes available'
        : used > cap ? (used - cap) + ' bytes over limit' : (cap - used) + ' bytes remaining';
      if (tone) left.dataset.tone = tone; else delete left.dataset.tone;
    }

    // Notices
    var status = panel.querySelector('[data-nfc-status]');
    var text = statusText(v, st);
    if (status) {
      status.hidden = !text;
      var statusSpan = status.querySelector('[data-nfc-status-text]');
      if (statusSpan) statusSpan.textContent = text;
      status.className = 'nfc-notice ' + (v.reason === 'loading' ? 'nfc-notice--grey' : 'nfc-notice--red');
    }
    var over = panel.querySelector('[data-nfc-over]');
    if (over) over.hidden = v.reason !== 'too_long';
    var tight = panel.querySelector('[data-nfc-tight]');
    if (tight) tight.hidden = !(/ST25TN01K/i.test(v.chip || '') && v.ok && used > 0 && pct >= 70);
    var same = panel.querySelector('[data-nfc-same]');
    if (same) {
      var keys = keysInOrder(host, p, variantId);
      same.textContent = keys > 1 ? 'All ' + keys + ' keychains get the same encoding.'
        : 'Every keychain gets the same encoding.';
    }

    // Save
    var save = panel.querySelector('[data-nfc-save]');
    if (save) {
      save.disabled = !v.ok;
      save.textContent = st.flash ? 'Saved' : 'Save encoding';
      save.toggleAttribute('data-saved', st.flash);
    }
  }

  /**
   * #1127: "Customize this design" for an option Studio has a linked design for. The
   * link opens that design in Quick Design (an ordinary custom order, charged the custom
   * price and minimum quantity), never the ready-made line being added.
   */
  function offerCustomize(host, p, variantId) {
    if (!p.customize || !p.link) return;
    if (host.dataset.nfcCustomizeFor === variantId) return;
    host.dataset.nfcCustomizeFor = variantId;
    p.customize.hidden = true;
    var app = resolveApp();
    var shop = window.Shopify && window.Shopify.shop;
    if (!app || !app.origin || !shop || !variantId) return;
    fetch(app.proxyPath + '/api/shopify/ready-made-template?variantId=' + encodeURIComponent(variantId), {
      credentials: 'same-origin',
    })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (json) {
        var id = json && typeof json.templateId === 'string' ? json.templateId : '';
        if (host.dataset.nfcCustomizeFor !== variantId || !TEMPLATE_ID_RE.test(id)) return;
        p.link.href = app.origin + '/embed?shop=' + encodeURIComponent(shop) + '&templateId=' + encodeURIComponent(id);
        p.customize.hidden = false;
      })
      .catch(function () { /* no link: the key can still be bought as ready-made */ });
  }

  function sync(host) {
    var p = partsOf(host);
    if (!p) return;
    var variantId = selectedVariantId(p.form);
    var on = encodes(host, variantId);
    host.classList.toggle('nfc-encoding-active', on);
    if (complete(p)) {
      render(host, p, variantId, on);
      if (on && capacities === undefined && resolveApp()) loadCapacities(resolveApp()).then(syncAll);
    }
    offerCustomize(host, p, variantId);
  }

  function syncAll() {
    document.querySelectorAll(HOST_SELECTOR).forEach(sync);
  }

  /** Saves the typed value when it is valid; returns whether the line may be added. */
  function readyToAdd(host, p) {
    var variantId = selectedVariantId(p.form);
    var st = stateOf(p.panel);
    var v = verdict(host, p, st, variantId);
    if (v.mifare) {
      render(host, p, variantId, true);
      return true;
    }
    if (v.ok) {
      st.saved = v.value;
      st.strict = false;
      st.open = false;
      var input = inputOf(p, st.tab);
      if (input) input.value = v.value;
      render(host, p, variantId, true);
      return true;
    }
    st.strict = true;
    st.open = true;
    render(host, p, variantId, true);
    var field = inputOf(p, st.tab);
    if (field && !field.disabled) field.focus();
    return false;
  }

  function encodingFieldset(p) {
    var index = p.panel.dataset.nfcEncodingIndex;
    return p.scope ? p.scope.querySelector('variant-picker fieldset[data-fieldset-index="' + index + '"]') : null;
  }

  document.addEventListener('click', function (event) {
    var target = event.target && event.target.closest ? event.target : null;
    if (!target) return;

    // Panel controls
    var control = target.closest('[data-nfc-tab], [data-nfc-save], [data-nfc-cancel]');
    if (control) {
      var cHost = hostOf(control);
      var cp = cHost && partsOf(cHost);
      if (!cp || !complete(cp)) return;
      var st = stateOf(cp.panel);
      var variantId = selectedVariantId(cp.form);
      if (control.matches('[data-nfc-tab]')) {
        st.tab = control.dataset.nfcTab;
        st.strict = false;
        render(cHost, cp, variantId, encodes(cHost, variantId));
        var tabInput = inputOf(cp, st.tab);
        if (tabInput && !tabInput.disabled) tabInput.focus();
      } else if (control.matches('[data-nfc-save]')) {
        var v = verdict(cHost, cp, st, variantId);
        if (!v.ok || v.mifare) return;
        st.saved = v.value;
        st.strict = false;
        st.flash = true;
        var savedInput = inputOf(cp, st.tab);
        if (savedInput) savedInput.value = v.value;
        render(cHost, cp, variantId, true);
        setTimeout(function () {
          st.flash = false;
          st.open = false;
          sync(cHost);
        }, SAVED_FLASH_MS);
      } else {
        st.saved = null;
        st.open = false;
        st.strict = false;
        var fieldset = encodingFieldset(cp);
        var radios = fieldset ? fieldset.querySelectorAll('input[type="radio"]') : [];
        for (var i = 0; i < radios.length; i++) {
          if (/^no\b/i.test(radios[i].value.trim())) { radios[i].click(); break; }
        }
        sync(cHost);
      }
      return;
    }

    // A click on the already-selected Custom Encoding pill reopens a saved panel.
    var label = target.closest('variant-picker .variant-option__button-label');
    if (label) {
      var radio = label.querySelector('input[type="radio"]');
      var lHost = hostOf(label);
      var lp = lHost && partsOf(lHost);
      if (radio && radio.checked && lp && complete(lp) &&
          label.closest('fieldset') === encodingFieldset(lp) && encodes(lHost, selectedVariantId(lp.form))) {
        stateOf(lp.panel).open = true;
        sync(lHost);
      }
      return;
    }

    // Add to Cart
    var button = target.closest('button[name="add"]');
    if (!button || button.type === 'button') return; // "Choose options" opens the product, nothing to add yet
    var host = button.closest(HOST_SELECTOR);
    if (!host) return;
    if (button.dataset.nfcConfirmed === '1') {
      delete button.dataset.nfcConfirmed;
      return;
    }
    var p = partsOf(host);
    if (!p) return;
    var addVariant = selectedVariantId(p.form);
    if (!encodes(host, addVariant)) return; // "No Encoding": the theme adds it as usual

    if (!complete(p)) {
      // A product card's direct add: there is nowhere to type the value.
      event.preventDefault();
      event.stopImmediatePropagation();
      if (host.dataset.nfcProductUrl) {
        window.location.href = host.dataset.nfcProductUrl + '?variant=' + encodeURIComponent(addVariant);
      }
      return;
    }

    var chip = parse(host.dataset.nfcChips)[addVariant];
    var mifare = Boolean(chip && window.NfckeyEncodingSize && window.NfckeyEncodingSize.isSectorFileChip(chip));
    if (mifare || capacities || !resolveApp()) {
      if (readyToAdd(host, p)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    // Limits not loaded (or the last load failed): hold the click, load, then add once the value fits.
    event.preventDefault();
    event.stopImmediatePropagation();
    loadCapacities(resolveApp()).then(function () {
      if (readyToAdd(host, p)) {
        button.dataset.nfcConfirmed = '1';
        button.click();
      }
    });
  }, true);

  document.addEventListener('input', function (event) {
    var input = event.target;
    if (!input || !input.matches) return;
    var isValue = input.matches('[data-nfc-input]');
    if (!isValue && !input.matches('input[name="quantity"]')) return;
    var host = hostOf(input);
    var p = host && partsOf(host);
    if (!p || !complete(p)) return;
    if (isValue) stateOf(p.panel).strict = false;
    sync(host);
  });

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || !input.matches || !input.matches('input[name="quantity"]')) return;
    var host = hostOf(input);
    if (host) sync(host);
  });

  // The theme sets the variant id input in its own variant:update handler; read it after.
  document.addEventListener('variant:update', function () { setTimeout(syncAll, 0); });

  // Quick add builds its modal from the product page after load.
  var pending = false;
  if (typeof MutationObserver === 'function') {
    new MutationObserver(function (records) {
      if (pending) return;
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType === 1 && (node.matches('[data-nfc-panel]') || node.querySelector('[data-nfc-panel]'))) {
            pending = true;
            setTimeout(function () { pending = false; syncAll(); }, 0);
            return;
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', syncAll);
  else syncAll();
})();
